import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdVerified } from 'react-icons/md';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { OfferRibbon } from './HospitalDetailPage';
import usePoll from '../hooks/usePoll';
import useLocation from '../hooks/useLocation';
import LocationChip from '../components/LocationChip';
import TrustFooter from '../components/TrustFooter';


function statusBadge(h) {
  if (h.isShutdown) return { label: '🔴 Shut Down', cls: 'badge-red' };
  if (h.is24Hours || h.is24x7) return { label: '🟢 Open 24/7', cls: 'badge-green' };
  // compute from OP hours
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const today = days[new Date().getDay()];
  const opDays = h.opDays || ['Mon','Tue','Wed','Thu','Fri','Sat'];
  if (!opDays.includes(today)) return { label: '🌙 Closed Today', cls: 'badge-amber' };
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = (h.opStart || '08:00').split(':').map(Number);
  const [eh, em] = (h.opEnd   || '18:00').split(':').map(Number);
  const start = sh * 60 + sm, end = eh * 60 + em;
  return nowMin >= start && nowMin < end
    ? { label: `🟢 Open · Closes ${h.opEnd || '18:00'}`, cls: 'badge-green' }
    : { label: `🟡 Closed · Opens ${h.opStart || '08:00'}`, cls: 'badge-amber' };
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const navigate2 = useNavigate();
  const { city, sortByDistance, distanceTo, coords } = useLocation();

  // Redirect first-time users to onboarding
  React.useEffect(() => {
    if (!localStorage.getItem('hcen_onboarded')) navigate2('/onboarding');
  }, [navigate2]);

  const [search, setSearch]       = useState('');
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [offers, setOffers]       = useState([]);   // global approved offers/camps


  // Hospitals + offers · poll every 2.5s so newly-added clinics and approved
  // health offers appear without a manual refresh. Skips when tab is hidden.
  usePoll(useCallback(async () => {
    const [hr, or] = await Promise.all([api.getHospitals(), api.getOffers({})]);
    setHospitals(hr.data || []);
    setOffers(Array.isArray(or.data) ? or.data : []);
    setLoading(false);
  }, []), 2500);



  // Split offers vs camps the same way HospitalDetailPage does.
  const isCamp = (o) => /camp|drive|checkup/i.test((o.badge || '') + ' ' + (o.title || ''));
  const homeCamps  = offers.filter(isCamp).slice(0, 8);
  const homePromos = offers.filter(o => !isCamp(o)).slice(0, 8);

  // Filter by search, then sort by distance if we have user coords.
  const _searched = hospitals.filter(h =>
    !search || h.name?.toLowerCase().includes(search.toLowerCase()) ||
    h.city?.toLowerCase().includes(search.toLowerCase())
  );
  // If a city is set, soft-prefer hospitals whose city/address contains it,
  // but never DROP others (some hospitals serve cross-city patients).
  const _cityRanked = city
    ? [..._searched].sort((a, b) => {
        const ac = `${a.city || ''} ${a.address || ''}`.toLowerCase().includes(city.toLowerCase()) ? -1 : 0;
        const bc = `${b.city || ''} ${b.address || ''}`.toLowerCase().includes(city.toLowerCase()) ? -1 : 0;
        return ac - bc;
      })
    : _searched;
  const filtered = coords ? sortByDistance(_cityRanked) : _cityRanked;

  return (
    <div className="fade-in">
      {/* Hero */}
      <div className="hero">
        <div style={{ maxWidth: 560 }}>
          <div style={{ marginBottom: 10 }}>
            <LocationChip />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div className="hero-title">Find Care,<br/>Book Instantly</div>
              <div className="hero-sub">
                {user ? `Welcome back, ${user.name || user.phone} 👋` : 'Book hospital OP & doctor appointments near you'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/emergency')}
              className="sos-ring"
              style={{
                flexShrink: 0,
                width: 64, height: 64, borderRadius: 16,
                border: '1.5px solid rgba(239,68,68,0.55)',
                background: 'rgba(239,68,68,0.14)',
                color: '#ef4444', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 18px rgba(239,68,68,0.25)',
              }}
              aria-label="Emergency SOS"
            >
              <span style={{ fontSize: 22 }}>📍</span>
              <span style={{ fontSize: 11, fontWeight: 900, letterSpacing: 1.1 }}>SOS</span>
            </button>
          </div>
          <div className="search-bar">
            <MdSearch />
            <input
              placeholder="Search hospitals, doctors, specialities…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && navigate(`/hospitals?q=${search}`)}
            />
          </div>
        </div>
      </div>

      <div className="page">
        {/* Quick actions — color-coded */}
        <div className="quick-actions-grid" style={{ marginBottom: 28 }}>
          {[
            { emoji: '🏥', label: 'Book OP',     path: '/hospitals',   cls: 'qa-red'   },
            { emoji: '👨‍⚕️', label: 'Find Doctor', path: '/find-doctor', cls: 'qa-green' },
            { emoji: '🧾', label: 'My Bookings', path: '/bookings',    cls: 'qa-blue'  },
            { emoji: '🚨', label: 'Emergency',   path: '/emergency',   cls: 'qa-amber' },
          ].map(a => (
            <button key={a.label} onClick={() => navigate(a.path)} className={`qa-card ${a.cls}`}>
              <div className="qa-icon"><span>{a.emoji}</span></div>
              {a.label}
            </button>
          ))}
        </div>



        {/* ── Health Camps (global, all hospitals) ──────────── */}
        {homeCamps.length > 0 && (
          <OfferRibbon
            title="🏕️ Health Camps Near You"
            items={homeCamps}
            accent="#10b981"
            onTap={(o) => o.hospitalId && navigate(`/hospitals/${o.hospitalId}`)}
          />
        )}

        {/* ── Offers / Promotions (global) ──────────────────── */}
        {homePromos.length > 0 && (
          <OfferRibbon
            title="🎁 Offers & Promotions"
            items={homePromos}
            accent="var(--red, #d32f2f)"
            onTap={(o) => o.hospitalId && navigate(`/hospitals/${o.hospitalId}`)}
          />
        )}

        {/* Nearby Hospitals */}
        <div className="section-title">🏥 Nearby Hospitals</div>
        <div className="section-sub">Showing {filtered.length} hospitals in your area</div>

        {loading ? <div className="spinner" /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.slice(0, 8).map(h => {
              const status = statusBadge(h);
              const km = coords ? distanceTo(Number(h.latitude ?? h.lat), Number(h.longitude ?? h.lng)) : null;
              return (
                <div key={h.id || h.hospitalId}
                  className="hospital-card"
                  onClick={() => navigate(`/hospitals/${h.id || h.hospitalId}`)}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    {(() => {
                      const img = h.photoUrl || h.logoUrl || (Array.isArray(h.imageUrls) && h.imageUrls[0]);
                      return img ? (
                        <img src={img} alt={h.name}
                          className="hospital-img"
                          style={{ objectFit: 'cover', background: '#0f172a' }}
                          onError={e => { e.target.replaceWith(Object.assign(document.createElement('div'), { className: 'hospital-img', textContent: '🏥' })); }} />
                      ) : (
                        <div className="hospital-img">🏥</div>
                      );
                    })()}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <div className="hospital-name">{h.name}</div>
                        {h.isTrusted && <MdVerified style={{ color: '#f59e0b', fontSize: 16 }} />}
                        {h.is24Hours && <span style={{ fontSize: 10, background: 'rgba(0,212,170,0.12)', color: 'var(--primary)', padding: '2px 6px', borderRadius: 6, fontWeight: 700 }}>24/7</span>}
                      </div>
                      <div className="hospital-addr">📍 {h.address || h.city || 'HCEN Network'}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span className={`badge ${status.cls}`}>{status.label}</span>
                        {km != null && Number.isFinite(km) && (
                          <span className="badge" style={{ background: '#eef2ff', color: '#4f46e5', fontWeight: 700 }}>
                            📍 {km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`} away
                          </span>
                        )}
                        {h.icuAvailable > 0 && <span className="badge badge-blue">🛏️ ICU: {h.icuAvailable}</span>}
                        {Number(h.rating) > 0 && <span className="badge badge-primary">⭐ {Number(h.rating).toFixed(1)}</span>}
                        <span className="badge" style={{ background: '#f3f4f6', color: '#6b7280' }}>{h.type || 'Private'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="empty">
                <div className="empty-icon">🔍</div>
                <div className="empty-title">No hospitals found</div>
                <div>Try a different search term</div>
              </div>
            )}
          </div>
        )}
              <TrustFooter />
      </div>
    </div>
  );
}
